import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Board,
  type BoardDetail,
  type Card,
  type Column,
  displayColumnName,
  useBoardDetail,
  useBoards,
  useMoveCardToBoard,
  useMoveColumnToBoard,
} from '@/entities/board';
import { FormError } from '@/shared/forms';
import { Button, Combobox, Dialog, ErrorState, LoadingState, useToast } from '@/shared/ui';

import styles from './MoveToBoardDialog.module.css';

/** Destination boards: every other board of the workspace. */
function useDestinations(source: Board) {
  const boards = useBoards(source.workspace_id ?? '', source.workspace_id !== null);
  return {
    query: boards,
    list: (boards.data ?? []).filter((board) => board.id !== source.id),
  };
}

function BoardSelect({
  boards,
  value,
  onChange,
}: {
  boards: Board[];
  value: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.field}>
      <span>{t('move.board')}</span>
      <Combobox
        label={t('move.board')}
        value={value}
        options={boards.map((board) => ({ value: board.id, label: board.name }))}
        onChange={onChange}
      />
    </div>
  );
}

/** The column to preselect on a board: its default column when still valid, else the first. */
function defaultColumn(columns: Column[], board: Board | undefined): string {
  const preferred = columns.find((c) => c.id === board?.default_column_id);
  return (preferred ?? columns[0])?.id ?? '';
}

interface CardProps {
  card: Card;
  source: BoardDetail;
  onClose: () => void;
  /** Called after a successful move (the card is no longer on this board). */
  onMoved: () => void;
}

/**
 * Move a card to a column of another board. This is a move, not a copy: the same card goes.
 * Labels belong to boards, so they are removed; the warning says so before anything happens.
 */
export function MoveCardToBoardDialog({ card, source, onClose, onMoved }: CardProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const destinations = useDestinations(source.board);
  const [boardId, setBoardId] = useState('');
  const [columnChoice, setColumnChoice] = useState<string | null>(null);
  const move = useMoveCardToBoard(source.board.id);

  const effectiveBoardId = boardId || destinations.list[0]?.id || '';
  const target = useBoardDetail(effectiveBoardId, effectiveBoardId !== '');
  const columns = target.data?.columns ?? [];
  const columnId =
    columnChoice && columns.some((c) => c.id === columnChoice)
      ? columnChoice
      : defaultColumn(columns, target.data?.board);
  const labelCount = card.label_ids.length;

  let body;
  if (destinations.query.isPending) body = <LoadingState />;
  else if (destinations.query.isError) body = <ErrorState error={destinations.query.error} />;
  else if (destinations.list.length === 0)
    body = <p className={styles.note}>{t('move.noBoards')}</p>;
  else {
    body = (
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          move.mutate(
            { cardId: card.id, boardId: effectiveBoardId, columnId },
            {
              onSuccess: () => {
                toast.show({
                  message: t('move.cardMoved', { board: target.data?.board.name ?? '' }),
                });
                onMoved();
              },
            },
          );
        }}
      >
        <p className={styles.note}>{t('move.cardNote')}</p>
        <BoardSelect
          boards={destinations.list}
          value={effectiveBoardId}
          onChange={(id) => {
            setBoardId(id);
            setColumnChoice(null);
          }}
        />
        <div className={styles.field}>
          <span>{t('move.column')}</span>
          <Combobox
            label={t('move.column')}
            value={columnId}
            disabled={target.isPending || columns.length === 0}
            options={columns.map((column) => ({
              value: column.id,
              label: displayColumnName(column, t('columns.calendar')),
            }))}
            onChange={setColumnChoice}
          />
        </div>
        {target.isError && <ErrorState error={target.error} />}
        {!target.isPending && columns.length === 0 && (
          <p className={styles.note}>{t('move.noColumns')}</p>
        )}
        {labelCount > 0 && (
          <p className={styles.warning} role="alert">
            {t('move.labelsWarning', { count: labelCount })}
          </p>
        )}
        <FormError error={move.error} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={move.isPending || columnId === ''}>
            {t('move.confirm')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    );
  }
  return (
    <Dialog title={t('move.cardTitle', { title: card.title })} onClose={onClose}>
      {body}
    </Dialog>
  );
}

interface ColumnProps {
  column: Column;
  source: BoardDetail;
  onClose: () => void;
}

/** Move a column with all its cards to the end of another board. */
export function MoveColumnToBoardDialog({ column, source, onClose }: ColumnProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const destinations = useDestinations(source.board);
  const [boardId, setBoardId] = useState('');
  const move = useMoveColumnToBoard(source.board.id);
  const effectiveBoardId = boardId || destinations.list[0]?.id || '';
  const cards = source.cards.filter((card) => card.placement.column_id === column.id);
  const labelled = cards.filter((card) => card.label_ids.length > 0).length;

  let body;
  if (destinations.query.isPending) body = <LoadingState />;
  else if (destinations.query.isError) body = <ErrorState error={destinations.query.error} />;
  else if (destinations.list.length === 0)
    body = <p className={styles.note}>{t('move.noBoards')}</p>;
  else {
    body = (
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          move.mutate(
            { columnId: column.id, boardId: effectiveBoardId },
            {
              onSuccess: () => {
                toast.show({
                  message: t('move.columnMoved', {
                    board: destinations.list.find((b) => b.id === effectiveBoardId)?.name ?? '',
                  }),
                });
                onClose();
              },
            },
          );
        }}
      >
        <p className={styles.note}>{t('move.columnNote', { count: cards.length })}</p>
        <BoardSelect boards={destinations.list} value={effectiveBoardId} onChange={setBoardId} />
        <p className={styles.note}>{t('move.columnHiddenNote')}</p>
        {labelled > 0 && (
          <p className={styles.warning} role="alert">
            {t('move.columnLabelsWarning', { count: labelled })}
          </p>
        )}
        <FormError error={move.error} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={move.isPending}>
            {t('move.confirm')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    );
  }
  return (
    <Dialog title={t('move.columnTitle', { name: column.name })} onClose={onClose}>
      {body}
    </Dialog>
  );
}
